//#region Function Imports
import React, { useEffect, useState } from 'react'
import { Platform } from 'react-native';
import DrawerNavigation from './MobileNav/DrawerNavigation';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useColorScheme, ImageBackground, SafeAreaView, View } from 'react-native';
import StackNavigation from './AuthenticationNav/StackNavigation';
import StackNavigationResetPassword from './AuthenticationNav/StackNavigationResetPassword';
import TabNavigationWeb from './WebNav/TabNavigationWeb';
import { NavigationContainer, DefaultTheme, createNavigationContainerRef } from "@react-navigation/native";
import { useAuthContext } from '../hooks/useAuthContext';
import { useSettings } from '../hooks/useSettings';
import CustomLabel from '../components/CustomLabel/CustomLabel';
import { useSignin } from '../hooks/useSignIn';
import { useAccountContext } from '../hooks/useAccountContext';
import CustomActivityIndicator from '../components/CustomActivityIndicator/CustomActivityIndicator';
import { OasysChatButton } from '../screens/ChatBotHelper/OasysChatBot';
//#endregion

export const navigationRef = createNavigationContainerRef();

export default function NavContainer() {

  let colorScheme = useColorScheme();
  colorScheme = 'light'

  //#region Theme Colours
  // * Main Color Palette for all the containers 
  const navTheme = {
    ...DefaultTheme,
    colors: {
      ...DefaultTheme.colors,
      background: '#F4F7EE',
      background_white: '#FFFFFF',
      background_disabled: '#CCCCCC',
      background_header: colorScheme === 'light' ? '#f5f5f5' : '#2D2B2B',
      background_gray: '#d3d3d3',
      background_inverted: colorScheme === 'light' ? '#121212' : '#808080',
      background_card: '#FFFFFF',
      border_card: '#E4EDD6',
      top_tab_nav_text: '#8A9E6A',
      top_tab_nav_underline: '#86BC25',
      card_line: colorScheme === 'light' ? '#000000' : '#FFFFFF',
      buttons: colorScheme === 'light' ? '#FFFFFF' : '#000000',
      buttons_primary: '#EEF4E2',
      boreder_color: '#e8e8e8',
      buttons_secondary: '#3B71F3',
      buttons_submit: '#86BC25',
      card_pending: '#FEF3C7',
      // * Text
      text_primary: '#1A2410',
      text_secondary: '#637A45',
      text_muted: '#8A9E6A',
      text_white: '#FFFFFF',
      text_approved: '#5C8F1A',
      text_pending: '#92400E',
      text_not_submitted: '#64748B',
      text_rejected: '#B91C1C',
      text_input: colorScheme === 'light' ? '#051C60' : '#FFFFFF',
      text_placeholder: '#C0CCA8',
      // * End Text
      // * Button Solid 
      background_rejected: '#FEE2E2',
      background_submitted: '#FEF3C7',
      background_not_submitted: '#F1F5F9',
      background_approved: '#EEF4E2',
      // * End Button Solid 
      //* Button Gradient
      green: '#86BC25',
      green_dark: '#5C8F1A',
      green_pale: '#E4EDD6',
      green_xpale: '#EEF4E2',
      //* End Button Gradient
      //* Warnings 
      error: '#E24B4A',
      notification: '#ff3b30',
      background_dropdown: colorScheme === 'light' ? '#ffffff' : '#121212',
      dropdown_border: colorScheme === 'light' ? '#e8e8e8' : '#e8e8e8',
      background_dropdown_with_icons: colorScheme === 'light' ? '#ffffff' : '#121212',
      white_or_transparent: colorScheme === 'light' ? '#ffffff' : '#RRGGBB',
      calendar_numbers: colorScheme === 'light' ? '#2d4150' : '#DBE9EE',
      calendarArrow: '#8C989A',
      calendarMonth: '#637A45',
      calendarDotColor: '#637A45',
      validation_error: colorScheme === 'light' ? '#b30f0f' : '#ed2c2c',
      language_selector: colorScheme === 'light' ? '#9bd530' : '#86BC25',
    },
  };
  //#endregion

  const { isSigned, resetPassword, dispatch: authDispatch } = useAuthContext();
  const { refreshSignIn } = useSignin();
  const { dispatch } = useAccountContext();
  const { isWinAuth } = useSettings();
  // * Defining routes as nested navigations
  const employeeTabScreens = {
    Dashboard: 'Dashboard',
    Assessment: 'Assessment',
    Leaves: 'Leaves',
    Surveys: 'Surveys/:surveyId?',
    Timesheets: 'Timesheets',
    MyTrainings: 'MyTrainings',
    Events: 'Events',
  };
  const linking = {
    prefixes: Platform.OS === 'web' ? [window.location.origin] : [],
    config: {
      screens: {
        home: { path: 'home', screens: employeeTabScreens },
        Home: { path: 'Home', screens: employeeTabScreens },
        HRAdmin: {
          path: 'HRAdmin',
          screens: {
            HRAdminPageEmployees: 'Employees',
            HRAdminPageCompany: 'Company',
            HRAdminPageReports: 'Reports',
            HRAdminPageTrainings: 'HRAdminTrainings',
            HRAdminPageClients: 'HRAdminClients',
            HRAdminPageUtilities: 'HRAdminUtilities',
            HRAdminPageAnalytics: 'HRAdminAnalytics',
            HomePageHRAdmin: 'Timesheets',
          },
        },
        Recruitment: {
          path: 'Recruitment',
          screens: {
            RecruitmentPageCandidates: 'Candidates',
            RecruitmentPageVacancies: 'Vacancies',
          },
        },
        Shifts: {
          path: 'Shifts',
          screens: {
            ShiftsAdmin: 'ShiftsAdmin',
          },
        },
        SettingsScreen: 'settings',
        MyAccountScreen: 'profile',
        HomePageZero: 'announcements',
        SignInScreen: 'sign-in',
        SignInScreen2FA: 'sign-in/verification',
        SignUpScreen: 'sign-up',
        UpdatePasswordScreen: 'update-password',
      }
    }
  };
  const [loadingScreen, setLoadingScreen] = useState(true);
  const [loadDrawer, setLoadDrawer] = useState("Home")


  // Token is stored in asyncstorage if that is true then keep logged in. In all API calls there is check if token is expired. 
  // If that is true then the application it will automatically log out
  useEffect(() => {
    const loadAppState = async () => {
      setLoadingScreen(true);

      try {
        const drawer = await AsyncStorage.getItem('drawer');
        if (drawer) {
          setLoadDrawer(drawer);
        }

        const rawLoginData = await AsyncStorage.getItem('login_data');
        const rawClientApi = await AsyncStorage.getItem('client_api');

        if (!rawLoginData || !rawClientApi) {
          dispatch({ type: 'RESET_STATE' });
          return;
        }

        let loginData = null;
        let clientApiData = null;

        try {
          loginData = JSON.parse(rawLoginData);
          clientApiData = JSON.parse(rawClientApi);
        } catch (e) {
          dispatch({ type: 'RESET_STATE' });
          authDispatch({ type: 'LOGOUT' });
          return;
        }

        if (!loginData?.responseData || !clientApiData?.clientApi) {
          dispatch({ type: 'RESET_STATE' });
          authDispatch({ type: 'LOGOUT' });
          return;
        }

        await isWinAuth(clientApiData.clientApi);
        // * Refresh Token on Refresh of Application if exception then reset 
        await refreshSignIn(loginData.responseData, clientApiData.clientApi);
      } catch (error) {
        console.log('Bootstrap failed:', error);
        dispatch({ type: 'RESET_STATE' });
        authDispatch({ type: 'LOGOUT' });
      } finally {
        setLoadingScreen(false);
      }
    };

    loadAppState();
  }, []);

  return (
    <NavigationContainer
      ref={navigationRef}
      theme={navTheme}
      linking={linking}
      // onStateChange={state => {
      //   console.log('Navigation State:', JSON.stringify(state, null, 2));
      // }}
      fallback={<CustomLabel>Loading...</CustomLabel>}>
      {/* <ImageBackground source={require("../img/background.png")} resizeMode="cover" style={{ width: "100%", flex: 1, justifyContent: "center" }}> */}
      <View style={{ flex: 1 }}>
        {loadingScreen ?
          <CustomActivityIndicator />
          : isSigned ?
            (resetPassword
            ? <StackNavigationResetPassword />
            : Platform.OS === 'web'
              ? <TabNavigationWeb />
              : <DrawerNavigation loadDrawer={loadDrawer} />)
            : <StackNavigation />
        }
        {isSigned && !loadingScreen && !resetPassword && process.env.AI_ENABLED === 'true' && <OasysChatButton />}
      </View>
      {/* </ImageBackground> */}
    </NavigationContainer >
  )
}
